import { useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { CoachMasterPackage } from '../types/CoachMasterData.type';
import { useIsMobile } from '../hooks/useResponsive';

// A compact Gantt-style timeline for a student's course packages. Each package
// is one bar spanning its registration start -> expiry date over a shared time
// axis, coloured by status, with a red "Today" line so the course history and
// what's currently active read at a glance.

const STATUS_COLORS = {
  // Ongoing (spans today) — active green, matching the app's ACTIVE status.
  ongoing: '#1f8a5b',
  // Upcoming (fully in the future) — info blue.
  upcoming: '#2563a8',
  // Expired (fully in the past) — muted neutral gray.
  expired: '#6b7280',
} as const;

const TODAY_COLOR = '#d3455b';

type PackageStatus = keyof typeof STATUS_COLORS;

// Expired bars carry a diagonal stripe on top of the gray so status never rests
// on colour alone (the gray↔blue distance is intentionally low — expired reads
// as "inactive"). Legend and bars share the same treatment.
const STRIPE_OVERLAY =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0, rgba(255,255,255,0.55) 2px, transparent 2px, transparent 5px)';

const statusFillSx = (status: PackageStatus) => ({
  backgroundColor: STATUS_COLORS[status],
  ...(status === 'expired' ? { backgroundImage: STRIPE_OVERLAY } : {}),
});

const STATUS_LABELS: Record<PackageStatus, string> = {
  ongoing: 'Ongoing',
  upcoming: 'Upcoming',
  expired: 'Expired',
};

const DAY_MS = 24 * 60 * 60 * 1000;

const formatDate = (value: number): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

const formatTick = (value: number): string => {
  const date = new Date(value);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${month} ${String(date.getFullYear()).slice(-2)}`;
};

const getStatus = (
  start: number,
  end: number,
  today: number,
): PackageStatus => {
  if (end < today) return 'expired';
  if (start > today) return 'upcoming';
  return 'ongoing';
};

export type TimelinePackage = CoachMasterPackage & {
  serviceType?: string;
  subtype?: string;
  serviceInvoiceName?: string;
  courseLevel?: string;
  visitType?: string;
  visitBalance?: number;
};

type ParsedPackage = {
  id: number;
  course: string;
  start: number;
  end: number;
  status: PackageStatus;
  package: TimelinePackage;
};

type CoursePackageTimelineProps = {
  packages: TimelinePackage[];
  onPackageClick?: (pkg: TimelinePackage) => void;
};

const CoursePackageTimeline = ({
  packages,
  onPackageClick,
}: CoursePackageTimelineProps) => {
  const today = useMemo(() => Date.now(), []);
  const isMobile = useIsMobile();

  const { rows, domainStart, domainEnd, ticks } = useMemo(() => {
    const parsed: ParsedPackage[] = packages
      .map((pkg) => {
        const start = new Date(pkg.registrationStartDate).getTime();
        const end = new Date(pkg.registrationEndDate).getTime();
        return { pkg, start, end };
      })
      .filter(({ start, end }) => !Number.isNaN(start) && !Number.isNaN(end))
      .map(({ pkg, start, end }) => ({
        id: pkg.id,
        course: pkg.course,
        start,
        package: pkg,
        // Guard against inverted ranges so a bar always has non-negative width.
        end: Math.max(end, start),
        status: getStatus(start, Math.max(end, start), today),
      }));

    if (parsed.length === 0) {
      return { rows: [], domainStart: 0, domainEnd: 0, ticks: [] as number[] };
    }

    // Domain covers every package and always includes today so the red line is
    // visible even when all courses are entirely in the past or future.
    let min = Math.min(...parsed.map((p) => p.start), today);
    let max = Math.max(...parsed.map((p) => p.end), today);
    // Pad the ends (≥ 7 days) so bars/labels don't touch the edges.
    const pad = Math.max((max - min) * 0.04, 7 * DAY_MS);
    min -= pad;
    max += pad;

    // 5 evenly spaced axis ticks across the domain.
    // Fewer axis ticks on phones so labels don't collide.
    const TICK_COUNT = isMobile ? 3 : 5;
    const tickValues = Array.from({ length: TICK_COUNT }, (_, i) =>
      Math.round(min + ((max - min) * i) / (TICK_COUNT - 1)),
    );

    return {
      rows: parsed,
      domainStart: min,
      domainEnd: max,
      ticks: tickValues,
    };
  }, [packages, today, isMobile]);

  if (rows.length === 0) return null;

  const span = domainEnd - domainStart || 1;
  const toPct = (value: number) =>
    Math.min(100, Math.max(0, ((value - domainStart) / span) * 100));
  const todayPct = toPct(today);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          Course Timeline
        </Typography>
        {/* Legend — status is never colour-alone. */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
        >
          {(Object.keys(STATUS_LABELS) as PackageStatus[]).map((status) => (
            <Stack
              key={status}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  ...statusFillSx(status),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {STATUS_LABELS[status]}
              </Typography>
            </Stack>
          ))}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 2, height: 12, backgroundColor: TODAY_COLOR }} />
            <Typography variant="caption" color="text.secondary">
              Today
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Axis header with tick labels */}
      <Box sx={{ position: 'relative', height: 18, mb: 0.5 }}>
        {ticks.map((tick, index) => {
          const left = toPct(tick);
          return (
            <Typography
              key={tick}
              variant="caption"
              color="text.secondary"
              sx={{
                position: 'absolute',
                left: `${left}%`,
                transform:
                  index === 0
                    ? 'none'
                    : index === ticks.length - 1
                      ? 'translateX(-100%)'
                      : 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontSize: 11,
              }}
            >
              {formatTick(tick)}
            </Typography>
          );
        })}
      </Box>

      {/* Plot area: gridlines + today line span all rows */}
      <Box sx={{ position: 'relative' }}>
        {/* Recessive vertical gridlines */}
        {ticks.map((tick) => (
          <Box
            key={tick}
            sx={(theme) => ({
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${toPct(tick)}%`,
              width: '1px',
              backgroundColor: theme.palette.action.hover,
              zIndex: 0,
            })}
          />
        ))}

        {/* Red "Today" line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${todayPct}%`,
            width: '2px',
            backgroundColor: TODAY_COLOR,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />

        {/* One row per package */}
        <Stack spacing={1} sx={{ position: 'relative', zIndex: 1, py: 0.5 }}>
          {rows.map((row) => {
            const left = toPct(row.start);
            const width = Math.max(toPct(row.end) - left, 1.5);
            const borderColor = alpha(STATUS_COLORS[row.status], 0.6);
            return (
              <Box key={row.id}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mb: 0.25,
                  }}
                  title={row.course}
                >
                  {row.course}
                </Typography>
                {/* Track */}
                <Box sx={{ position: 'relative', height: 16 }}>
                  <Tooltip
                    arrow
                    title={
                      <Box sx={{ fontSize: 12 }}>
                        <div>{row.course}</div>
                        <div>
                          {formatDate(row.start)} – {formatDate(row.end)}
                        </div>
                        <div>{STATUS_LABELS[row.status]}</div>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${left}%`,
                        width: `${width}%`,
                        minWidth: 6,
                        borderRadius: '4px',
                        ...statusFillSx(row.status),
                        border: `1px solid ${borderColor}`,
                        boxSizing: 'border-box',
                        cursor: onPackageClick ? 'pointer' : 'default',
                        transition: 'filter 0.15s ease',
                        '&:hover': { filter: 'brightness(0.92)' },
                      }}
                      role={onPackageClick ? 'button' : undefined}
                      tabIndex={onPackageClick ? 0 : undefined}
                      aria-label={
                        onPackageClick
                          ? `Open service ${row.course}`
                          : undefined
                      }
                      onClick={() => onPackageClick?.(row.package)}
                      onKeyDown={(event) => {
                        if (
                          onPackageClick &&
                          (event.key === 'Enter' || event.key === ' ')
                        ) {
                          event.preventDefault();
                          onPackageClick(row.package);
                        }
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default CoursePackageTimeline;
